<template>
    <div class="profile-page w-100">
        <Sidebar class="strip-content" />
        <div class="page-content">
            <PageHeader pageName="Account settings" />
            <div class="profile-page-form px-4 py-3 mx-lg-3" v-if="user">
                <!-- Subscription box - Mobile -->
                <div class="subscription-info-box less-desktop-only mb-3" v-if="user.is_company || user.is_agent">
                    <div class="d-flex justify-content-between">
                        <div>
                            <h4>Current subscription</h4>
                            <p class="mt-2 text-large">
                                {{ currentSubscriptionPlan.name }}
                            </p>
                        </div>

                        <a v-if="user.prospecting_tool_attributes && user.prospecting_tool_attributes.canUpgradeSubscription"
                            href="#" class="btn btn--secondary-v2 btn--xs align-items-center gap-2"
                            @click.prevent="$root.$emit('open-activate-premium-plan-modal')">
                            Upgrade
                            <svg width="10" height="17" viewBox="0 0 10 17" fill="none"
                                xmlns="http://www.w3.org/2000/svg">
                                <path d="M1.18202 0.817977L8.72782 8.36377L1.18202 15.9096" stroke="white"
                                    fill="none" />
                            </svg>
                        </a>
                    </div>

                    <h3 class="mb-1">£{{ currentSubscriptionPlan.subscriptionPriceWithVAT.toFixed(2) }} per month </h3>

                    <a v-if="currentSubscriptionPlan.type" href="#" class="d-flex text-small w-fit"
                        style="color: var(--text-color); text-decoration: none;"
                        @click.prevent="showSubscriptionDetails = !showSubscriptionDetails">
                        <template v-if="!showSubscriptionDetails">
                            <span>Show details</span>
                            <i class="ri-arrow-right-s-line" style="font-size: 16px;"></i>
                        </template>

                        <template v-else>
                            <span>Hide details</span>
                            <i class="ri-arrow-down-s-line" style="font-size: 16px;"></i>
                        </template>
                    </a>

                    <Transition name="fade">
                        <div class="subscription-details flex-column gap-1 mt-2" v-show="showSubscriptionDetails">
                            <div class="d-flex justify-content-between">
                                <span>{{ currentSubscriptionPlan.nameShort }}</span>
                                <span>£{{ currentSubscriptionPlan.subscriptionPrice.toFixed(2) }}</span>
                            </div>
                            <div class="d-flex justify-content-between">
                                <strong>Subtotal</strong>
                                <strong>£{{ currentSubscriptionPlan.subscriptionPrice.toFixed(2) }}</strong>
                            </div>
                            <hr class="my-1" style="border-bottom: 1px solid var(--text-color); opacity: 0.5;" />
                            <div class="d-flex justify-content-between">
                                <span>VAT</span>
                                <span>£{{ currentSubscriptionPlan.subscriptionVAT.toFixed(2) }}</span>
                            </div>
                            <div class="d-flex justify-content-between">
                                <strong>Total</strong>
                                <strong>£{{ currentSubscriptionPlan.subscriptionPriceWithVAT.toFixed(2) }}</strong>
                            </div>
                            <div class="d-flex flex-wrap gap-2">
                                <a class="btn btn--primary btn--xs" :href="currentSubscriptionPlan.contractUrl">
                                    Download agreement
                                </a>
                                <a class="btn btn--primary btn--xs" href="/purchase-credits?redirectSuccessTo=profile"
                                    v-if="user.prospecting_tool_attributes && !user.prospecting_tool_attributes.isPendingSubscription">
                                    Add credits
                                </a>
                            </div>
                        </div>
                    </Transition>

                    <div
                        v-if="user.prospecting_tool_attributes && user.prospecting_tool_attributes.isPendingSubscription">
                        <p class="mb-1">Direct debit outstanding</p>
                        <a class="btn btn--secondary btn--xs"
                            :href="user.prospecting_tool_attributes.pendingSubscriptionUrl">
                            Complete Direct Debit mandate
                        </a>
                    </div>
                </div>

                <p>{{ user.is_company ? 'Company' : '' }} Logo</p>
                <div class="user-actions d-flex align-items-center justify-content-between gap-3 mb-3 mt-4">
                    <div class="position-relative">
                        <img v-if="user.image_url" class="rounded-2 cursor-pointer" width="100"
                            @click.prevent="uploadPhoto" :src="user.image_url" :alt="user.public_name" />
                        <div v-else class="profile-page-avatar cursor-pointer position-relative rounded-2"
                            @click.prevent="uploadPhoto">
                            <span class="owner__initials">
                                {{ user.public_name_initials }}
                            </span>
                        </div>
                        <UploadPhoto ref="uploadPhoto" class="d-none" />
                        <div class="position-absolute p-2 d-flex justify-content-center align-items-center cursor-pointer"
                            @click.prevent="uploadPhoto"
                            style="top: -20px; right: -20px; background: var(--cb); border-radius: 100%; width: 40px; height: 40px;">
                            <i class="ri-edit-2-fill" style="color: #fff; font-size: 16px;"></i>
                        </div>
                    </div>

                    <!-- Additional actions container -->
                    <div class="d-flex flex-column flex-lg-row align-items-center justify-content-center gap-3">
                        <a class="btn btn--xs btn--secondary-v2-empty change-password-button" href="#"
                            @click.prevent="showPasswordModal = true">Change
                            password</a>
                    </div>
                </div>

                <div class="main-form form-group gap-4"
                    :class="{ 'main-form-content': !user.is_individual_tenant || formData.individual_type === 'landlord' }">
                    <div class="w-100">
                        <template v-if="user.is_company">
                            <div class="form-group">
                                <label class="form-label required">Company Name</label>
                                <input type="text" name="company_name" key="company_name"
                                    v-model="formData.company_name" class="form-control"
                                    :class="{ 'has-error': errors.first('company_name') }"
                                    v-validate="{ required: true, min: 1, max: 50 }" data-vv-as="company name" />
                                <div class="form-error">{{ errors.first('company_name') }}</div>
                            </div>
                        </template>
                        <div class="grid-2-col position-relative mt-4">
                            <template v-if="!user.is_company">
                                <div class="form-group">
                                    <label class="form-label required">First Name</label>
                                    <input type="text" name="firstname" key="firstname" v-model="formData.firstname"
                                        class="form-control" :class="{ 'has-error': errors.first('firstname') }"
                                        v-validate="{ required: true, min: 1, max: 20 }" data-vv-as="first name" />
                                    <div class="form-error first-name-error">{{ errors.first('firstname') }}</div>
                                </div>
                                <div class="form-group last-name">
                                    <label class="form-label required">Last Name</label>
                                    <input type="text" name="lastname" key="lastname" v-model="formData.lastname"
                                        class="form-control" :class="{ 'has-error': errors.first('lastname') }"
                                        v-validate="{ required: true, min: 1, max: 20 }" data-vv-as="last name" />
                                    <div class="form-error">{{ errors.first('lastname') }}</div>
                                    <div class="position-absolute" style="left: 0">
                                        <label>
                                            <input type="checkbox" v-model="formData.show_lastname_on_ads"
                                                :checked="formData.show_lastname_on_ads" />
                                            Show last name on my adverts
                                        </label>
                                    </div>
                                </div>
                            </template>
                            <div class="form-group" v-if="user.is_company" key="crm">
                                <label class="form-label required">Current CRM</label>
                                <input class="form-control" :class="{ 'has-error': errors.first('crm') }"
                                    autocomplete="off" name="crm" v-model="formData.crm" v-validate="{ required: true }"
                                    data-vv-as="crm" />
                                <span class="form-error">{{ errors.first('crm') }}</span>
                            </div>
                            <div class="form-group" :class="{ 'mt-4': !user.is_company }">
                                <label class="form-label required">Phone no.</label>
                                <div class="phone-wrapper">
                                    <input type="tel" ref="phone_number_e164" name="phone_number_e164"
                                        class="form-control" :class="{ 'has-error': errors.first('phone_number_e164') }"
                                        v-model="formData.phone_number_e164" v-validate="{ required: true }"
                                        data-vv-as="phone number" />
                                    <span class="info-note vertical-center-content"
                                        :class="user.has_verified_phone_number ? 'info-note--success' : 'info-note--danger'">
                                        <a v-if="!user.has_verified_phone_number" href="#"
                                            @click.prevent="$root.$emit('open-start-phone-verification-modal')">
                                            ⓘ Verify
                                        </a>
                                        <template v-else> ✓ Verified </template>
                                    </span>
                                </div>
                                <div class="form-error">{{ errors.first('phone_number_e164') }}</div>
                            </div>
                            <template v-if="user.is_individual">
                                <div class="form-group mt-4">
                                    <label class="form-label required">Individual type</label>
                                    <v-select class="form-control" name="individual_type" :options="individualTypes"
                                        :reduce="(options) => String(options.slug)" :searchable="false"
                                        :clearable="false" v-model="formData.individual_type"
                                        v-validate="{ required: true }" data-vv-as="individual type"
                                        @input="isTenant = formData.individual_type === 'tenant'">
                                    </v-select>
                                    <span class="form-error">{{ errors.first('individual_type') }}</span>
                                </div>
                            </template>
                            <div class="form-group" :class="{ 'mt-4': user.is_agent }"
                                v-if="user.is_company || user.is_agent || (user.is_individual && !isTenant)">
                                <label class="form-label required">Deposit scheme</label>
                                <v-select class="form-control" :class="{ 'has-error': errors.first('deposit_scheme') }"
                                    name="deposit_scheme" :options="depositSchemes"
                                    :reduce="(options) => String(options.slug)" :searchable="false" :clearable="false"
                                    v-model="formData.deposit_scheme" v-validate="{ required: true }"
                                    data-vv-as="deposit scheme"></v-select>
                                <span class="form-error">{{ errors.first('deposit_scheme') }}</span>
                            </div>
                            <div class="form-group" v-if="user.is_individual && isTenant">
                                <label class="form-label">Occupation</label>
                                <input class="form-control" :class="{ 'has-error': errors.first('occupation') }"
                                    autocomplete="off" name="occupation" v-model="formData.occupation"
                                    data-vv-as="occupation" />
                                <span class="form-error">{{ errors.first('occupation') }}</span>
                            </div>
                            <div class="form-group" v-if="user.is_individual && isTenant">
                                <label class="form-label">Household Income (annual)</label>
                                <v-select class="form-control" :class="{ 'has-error': errors.first('income') }"
                                    :options="incomes" :reduce="(options) => String(options.slug)" :searchable="false"
                                    :clearable="false" v-model="formData.income"></v-select>
                                <span class="form-error">{{ errors.first('income') }}</span>
                            </div>
                            <div class="form-group" v-if="user.is_company">
                                <label class="form-label required">Agent Affiliate Group</label>
                                <v-select class="form-control"
                                    :class="{ 'has-error': errors.first('agent_affiliate_group') }"
                                    :options="agentAffiliateGroups" :reduce="(options) => String(options.slug)"
                                    :searchable="false" :clearable="false"
                                    v-model="formData.agent_affiliate_group"></v-select>
                                <span class="form-error">{{ errors.first('agent_affiliate_group') }}</span>
                            </div>
                            <div class="form-group" v-if="user.is_individual && !isTenant">
                                <label class="form-label">Acredited Landlord Body</label>
                                <v-select class="form-control"
                                    :class="{ 'has-error': errors.first('acredited_landlord_body') }"
                                    :options="acreditedLandlordBodies" :reduce="(options) => String(options.slug)"
                                    :clearable="false" :searchable="false" v-model="formData.acredited_landlord_body"
                                    @input="formData.acredited_landlord_body_description = null"></v-select>
                                <span class="form-error">{{ errors.first('acredited_landlord_body') }}</span>
                            </div>
                            <div class="form-group" v-if="user.is_individual && isTenant">
                                <label class="form-label">Budget</label>
                                <div class="input-prepend" data-text="£">
                                    <input class="form-control" :class="{ 'has-error': errors.first('budget_min') }"
                                        autocomplete="off" name="budget_min" v-model="formData.budget_min"
                                        v-validate="{ numeric: true }" data-vv-as="minimum budget" placeholder="Min" />
                                </div>
                                <span class="form-error">{{ errors.first('budget_min') }}</span>
                            </div>
                            <div class="form-group" v-if="user.is_individual && isTenant">
                                <label class="form-label">&nbsp;</label>
                                <div class="input-prepend" data-text="£">
                                    <input class="form-control" :class="{ 'has-error': errors.first('budget_max') }"
                                        autocomplete="off" name="budget_max" v-model="formData.budget_max"
                                        v-validate="{ numeric: true }" data-vv-as="maximum budget" placeholder="Max" />
                                </div>
                                <span class="form-error">{{ errors.first('budget_max') }}</span>
                            </div>
                        </div>
                        <div class="form-group" v-if="user.is_company">
                            <label class="form-label required">Address</label>
                            <input class="form-control" :class="{ 'has-error': errors.first('address') }"
                                autocomplete="off" name="address" v-model="formData.address"
                                v-validate="{ required: true }" data-vv-as="address" />
                            <span class="form-error">{{ errors.first('address') }}</span>
                        </div>
                        <div class="form-group"
                            v-if="user.is_individual_landlord && !isTenant && formData.acredited_landlord_body === acreditedLandlordBodies.length - 1">
                            <label class="form-label">Please specify (Acredited Landlord Body Description):</label>
                            <input class="form-control"
                                :class="{ 'has-error': errors.first('acredited_landlord_body_description') }"
                                autocomplete="off" name="acredited_landlord_body_description"
                                v-model="formData.acredited_landlord_body_description" />
                            <span class="form-error">{{ errors.first('acredited_landlord_body_description') }}</span>
                        </div>
                        <div class="form-group" v-if="user.is_individual && isTenant">
                            <button class="btn btn--secondary-empty w-100" @click="showSelectAreaModal = true"
                                :style="[{ fontSize: 'var(--font-small)' }]">
                                <span v-if="!selectedAreas.length">Select areas of interest</span>
                                <span v-else>Edit areas of interest</span>
                            </button>

                            <ul class="tags-list pt-2" v-if="selectedAreas.length">
                                <li selected class="tag-item" v-for="(area, key) in selectedAreas" :key="`area-${key}`"
                                    @click.prevent="selectedAreas.splice(key, 1)">
                                    {{ area.formatted_name }}
                                </li>
                            </ul>
                            <div class="form-error">{{ errors.first('areas') }}</div>

                            <SelectAreaModal :show-modal="showSelectAreaModal" v-model="selectedAreas"
                                @close="showSelectAreaModal = false" />
                        </div>
                        <div class="grid-2-col mt-4" v-if="user.is_individual && isTenant">
                            <div class="form-group">
                                <label class="form-label">Move In (Earliest)</label>
                                <DatePicker name="move_in_earliest"
                                    :class="{ 'has-error': errors.first('move_in_earliest') }"
                                    v-model="formData.move_in_earliest" data-vv-as="move in earliest"
                                    :allow-past-dates="true">
                                </DatePicker>
                                <div class="form-error">{{ errors.first('move_in_earliest') }}</div>
                            </div>
                            <div class="form-group">
                                <label class="form-label">Move In (Latest)</label>
                                <DatePicker name="move_in_latest"
                                    :class="{ 'has-error': errors.first('move_in_latest') }"
                                    v-model="formData.move_in_latest" data-vv-as="move in latest"
                                    :allow-past-dates="true"></DatePicker>
                                <div class="form-error">{{ errors.first('move_in_latest') }}</div>
                            </div>
                        </div>

                        <div class="form-group">
                            <label class="form-label">Email</label>
                            <input class="form-control" :class="{ 'has-error': errors.first('email') }"
                                autocomplete="off" name="email" type="email" v-model="formData.email"
                                v-validate="{ required: true }" data-vv-as="email">
                        </div>

                        <div class="form-group mb-0">
                            <label class="form-label">{{ user.is_company ? 'Enquiries' : 'Notifications' }}
                                Email</label>
                            <input class="form-control" :class="{ 'has-error': errors.first('email_notifications') }"
                                autocomplete="off" name="email_notifications" type="email"
                                v-model="formData.email_notifications" v-validate="{ required: true }"
                                data-vv-as="enquiries email">
                            <span class="form-error">{{ errors.first('email_notifications') }}</span>
                        </div>

                        <div class="mb-4 span-col-2">
                            <div class="form-error">
                                When you change your email address you need to verify it in order to use it.
                                You'll receive an email for verification.
                            </div>
                            <span class="form-error">{{ errors.first('email') }}</span>
                        </div>

                        <div class="d-flex gap-4 justify-content-between span-col-2">
                            <div class="form-group w-100 mb-0">
                                <label class="form-label required">Password</label>
                                <div class="password-wrapper">
                                    <input class="form-control" :class="{ 'has-error': errors.first('password') }"
                                        autocomplete="new-password" :type="!showPasswords && 'password'" name="password"
                                        v-model="formData.password" v-validate="{ required: true }"
                                        data-vv-as="password">
                                    <i @click.prevent="showPasswords = !showPasswords"
                                        :class="{ 'ri-eye-line': !showPasswords, 'ri-eye-off-line': showPasswords }" />
                                </div>
                            </div>
                        </div>

                        <div class="d-flex flex-column">
                            <span class="form-error">{{ errors.first('password') }}</span>
                            <a href="#" @click.prevent="
                                $root.$emit('open-forgot-password-modal');
                            " class="mt-2" style="width: fit-content">Forgot Password?</a>
                        </div>
                    </div>
                    <div class="w-100 h-100 d-flex flex-column gap-5">
                        <!-- Subscription box - Desktop -->
                        <div class="subscription-info-box over-desktop-only" v-if="user.is_company || user.is_agent">
                            <div class="d-flex justify-content-between">
                                <div>
                                    <h4>Current subscription</h4>
                                    <p class="mt-2 text-large">{{ currentSubscriptionPlan.name }}</p>
                                </div>

                                <a v-if="user.prospecting_tool_attributes && user.prospecting_tool_attributes.canUpgradeSubscription"
                                    href="#" class="btn btn--secondary-v2 btn--xs align-items-center gap-2"
                                    @click.prevent="$root.$emit('open-upgrade-plan-modal')">
                                    Upgrade
                                    <svg width="10" height="17" viewBox="0 0 10 17" fill="none"
                                        xmlns="http://www.w3.org/2000/svg">
                                        <path d="M1.18202 0.817977L8.72782 8.36377L1.18202 15.9096" stroke="white"
                                            fill="none" />
                                    </svg>
                                </a>
                            </div>

                            <h3 class="mb-1">£{{ currentSubscriptionPlan.subscriptionPriceWithVAT.toFixed(2) }} per
                                month</h3>

                            <a v-if="currentSubscriptionPlan.type" href="#" class="d-flex text-small w-fit"
                                style="color: var(--text-color); text-decoration: none;"
                                @click.prevent="showSubscriptionDetails = !showSubscriptionDetails">
                                <template v-if="!showSubscriptionDetails">
                                    <span>Show details</span>
                                    <i class="ri-arrow-right-s-line" style="font-size: 16px;"></i>
                                </template>

                                <template v-else>
                                    <span>Hide details</span>
                                    <i class="ri-arrow-down-s-line" style="font-size: 16px;"></i>
                                </template>
                            </a>

                            <Transition name="fade">
                                <div class="subscription-details flex-column gap-1 mt-2"
                                    v-show="showSubscriptionDetails">
                                    <div class="d-flex justify-content-between">
                                        <span>{{ currentSubscriptionPlan.nameShort }}</span>
                                        <span>£{{ currentSubscriptionPlan.subscriptionPrice.toFixed(2) }}</span>
                                    </div>
                                    <div class="d-flex justify-content-between">
                                        <strong>Subtotal</strong>
                                        <strong>£{{ currentSubscriptionPlan.subscriptionPrice.toFixed(2) }}</strong>
                                    </div>
                                    <hr class="my-1"
                                        style="border-bottom: 1px solid var(--text-color); opacity: 0.5;" />
                                    <div class="d-flex justify-content-between">
                                        <span>VAT</span>
                                        <span>£{{ currentSubscriptionPlan.subscriptionVAT.toFixed(2) }}</span>
                                    </div>
                                    <div class="d-flex justify-content-between">
                                        <strong>Total</strong>
                                        <strong>£{{ currentSubscriptionPlan.subscriptionPriceWithVAT.toFixed(2)
                                        }}</strong>
                                    </div>
                                    <div class="d-flex flex-wrap gap-2">
                                        <a class="btn btn--primary btn--xs" :href="currentSubscriptionPlan.contractUrl">
                                            Download agreement
                                        </a>
                                        <!-- TODO: Add link -->
                                        <a class="btn btn--primary btn--xs"
                                            href="/purchase-credits?redirectSuccessTo=profile"
                                            v-if="user.prospecting_tool_attributes && !user.prospecting_tool_attributes.isPendingSubscription">
                                            Add credits
                                        </a>
                                    </div>
                                </div>
                            </Transition>

                            <div
                                v-if="user.prospecting_tool_attributes && user.prospecting_tool_attributes.isPendingSubscription">
                                <p class="mb-1">Direct debit outstanding</p>
                                <a class="btn btn--secondary btn--xs"
                                    :href="user.prospecting_tool_attributes.pendingSubscriptionUrl">
                                    Complete Direct Debit mandate
                                </a>
                            </div>
                        </div>
                        <div class="form-group" :class="{ 'h-50 mt-auto': user.is_company }"
                            v-if="!user.is_individual_tenant || formData.individual_type === 'landlord'">
                            <div class="textarea-wrapper">
                                <label class="form-label" :class="{ required: user.is_company }">
                                    {{ user.is_company ? 'Company description' : 'About Me' }}
                                </label>
                                <textarea class="form-control" :class="{ 'has-error': errors.first('description') }"
                                    autocomplete="off" name="description" v-model="formData.description"
                                    v-validate="{ required: user.is_company }" data-vv-as="description"
                                    maxlength="512"></textarea>
                            </div>
                            <span class="form-error">{{ errors.first('description') }}</span>
                        </div>
                    </div>
                </div>

                <div class="d-flex justify-content-between">
                    <div class="mt-auto">
                        <button type="button" class="btn btn--secondary" :disabled="isLoading"
                            @click.prevent="onSave">Save</button>
                    </div>

                    <a href="#" class="color-red d-block" @click.stop="showDeleteModal = true"
                        style="width: fit-content">
                        Delete my account
                    </a>
                </div>
            </div>
        </div>
        <UserPublicProfileModal :show-modal="showPublicProfileModal" @close="showPublicProfileModal = false" />
        <ChangePasswordModal :show-modal="showPasswordModal" @close="showPasswordModal = false" />
        <DeleteUserModal :show-modal="showDeleteModal" @close="showDeleteModal = false" />
    </div>
</template>

<script>
import SelectAreaModal from '@components/AdForm/partials/SelectAreaModal.vue';
import DatePicker from '@components/partials/date/DatePicker';
import PageHeader from '@components/partials/PageHeader';
import Sidebar from '@components/partials/Sidebar.vue';
import ChangePasswordModal from '@components/UserProfile/partials/ChangePasswordModal.vue';
import DeleteUserModal from '@components/UserProfile/partials/DeleteUserModal.vue';
import UploadPhoto from '@components/UserProfile/partials/UploadPhoto.vue';
import UserPublicProfileModal from '@components/UserProfile/UserPublicProfileModal.vue';
import telInput from '@helpers/tel-input';
import vSelect from 'vue-select';
import { mapMutations, mapState } from 'vuex';

export default {
    inject: ['$validator'],
    components: {
        ChangePasswordModal,
        DeleteUserModal,
        UploadPhoto,
        vSelect,
        DatePicker,
        SelectAreaModal,
        PageHeader,
        UserPublicProfileModal,
        Sidebar,
    },
    data() {
        return {
            showEditModal: false,
            showPasswordModal: false,
            showDeleteModal: false,
            showPublicProfileModal: false,
            conversationsNotificationsCount: 0,
            formData: {
                company_name: '',
                firstname: '',
                lastname: '',
                show_lastname_on_ads: false,
                phone_number_e164: '',
                show_phone_on_ads: true,
                individual_type: '',
                crm: '',
                deposit_scheme: '',
                occupation: '',
                agent_affiliate_group: '',
                income: '',
                acredited_landlord_body: '',
                acredited_landlord_body_description: '',
                budget_min: '',
                budget_max: '',
                address: '',
                areas: [],
                move_in_earliest: '',
                move_in_latest: '',
                description: '',
                email: '',
                email_notifications: '',
                password: '',
            },
            isTenant: false,
            iti: null,
            isLoading: false,
            selectedAreas: [],
            showSelectAreaModal: false,
            showPasswords: false,
            showSubscriptionDetails: false,
        };
    },
    computed: {
        ...mapState('user', ['user']),
        ...mapState(['depositSchemes', 'acreditedLandlordBodies', 'incomes', 'agentAffiliateGroups', 'individualTypes']),
        currentSubscriptionPlan() {
            if (this.user.prospecting_tool_attributes && this.user.prospecting_tool_attributes.currentSubscriptionPlan.type) return this.user.prospecting_tool_attributes.currentSubscriptionPlan;
            return {
                name: 'Freemium Plan',
                type: null,
                subscriptionPriceWithVAT: 0,
                subscriptionPrice: 0,
                subscriptionVAT: 0,
                nameShort: 'Freemium Plan',
            };
        },
    },
    updated() {
        if (!this.iti) {
            this.iti = telInput(this.$refs.phone_number_e164);

            if (this.user.phone_number_e164) {
                this.iti.setNumber(this.user.phone_number_e164);
            }
        }
    },
    watch: {
        user(user) {
            if (!user) {
                return;
            }
            this.conversationsNotificationsCount = user.conversations_notifications_count;
            this.listenForNotifications(user.uuid);
            this.resetForm();
        },
        selectedAreas(areas) {
            if (areas.length) {
                this.$validator.errors.remove('areas');
            }
            this.formData.areas = [];
            areas.forEach((item) => {
                this.formData.areas = this.formData.areas.concat(item.place_id.split(', '));
            });
        },
    },
    methods: {
        uploadPhoto() {
            this.$refs.uploadPhoto.$refs.photo.click();
        },
        listenForNotifications(userUuid) {
            window.LaravelEcho.private(`user.chat.notifications.${userUuid}`).listen('.newChatMessage', (e) => {
                const { conversationsNotificationsCount } = e;
                this.conversationsNotificationsCount = conversationsNotificationsCount;
            });
        },
        ...mapMutations('user', ['setUser']),
        onSave() {
            this.isLoading = true;
            const requests = [
                this.axios.patch('/api/user/update',
                    { ...this.formData, phone_number_e164: this.iti.getNumber() }),
            ];

            if (this.fields.email.changed || (this.fields.email_notifications && this.fields.email_notifications.changed)) {
                requests.push(
                    this.axios.patch('/api/user/email',
                        {
                            email: this.formData.email,
                            email_notifications: this.formData.email_notifications,
                            password: this.formData.password,
                        }),
                );
            }

            Promise.all(requests).then((res) => {
                let toastMessage = 'Profile succesfully updated.';
                if (res[0] && res[0].data && res[0].data.data) {
                    this.setUser(res[0].data.data);
                    if (!this.user.has_verified_phone_number) {
                        toastMessage += '\n\nPlease verify your new phone number.';
                    }
                }
                if (res[1] && res[1].data && res[1].data.changed) {
                    toastMessage += '\n\nPlease verify your new email address.';
                }
                window.showToastSuccess(toastMessage);
                this.formData.password = '';
                this.$validator.reset();
            }).finally(() => {
                this.isLoading = false;
            });
        },
        resetForm() {
            this.formData = {
                company_name: this.user.company_name,
                firstname: this.user.firstname,
                lastname: this.user.lastname,
                show_lastname_on_ads: this.user.show_lastname_on_ads,
                phone_number_e164: this.user.phone_number_e164,
                show_phone_on_ads: true,
                individual_type: String(this.user.individual_type || ''),
                crm: this.user.crm,
                deposit_scheme: String(this.user.deposit_scheme || ''),
                occupation: this.user.occupation,
                agent_affiliate_group: String(this.user.agent_affiliate_group || ''),
                income: String(this.user.income || ''),
                acredited_landlord_body: String(this.user.acredited_landlord_body || ''),
                acredited_landlord_body_description: this.user.acredited_landlord_body_description,
                budget_min: this.user.budget_min,
                budget_max: this.user.budget_max,
                address: this.user.address,
                areas: this.user.selectedAreas || [],
                move_in_earliest: this.user.move_in_earliest || '',
                move_in_latest: this.user.move_in_latest || '',
                description: this.user.description,
                email: this.user.email,
                email_notifications: this.user.email_notifications || this.user.email,
                password: '',
            };

            this.isTenant = this.formData.individual_type === 'tenant';

            this.selectedAreas = this.user.selectedAreas || [];

            this.$validator.reset();
        },
    },
};
</script>
